import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Trophy, Copy } from 'lucide-react';
import {
  getMyDonateChallenge,
  createDonateChallenge,
  updateDonateChallenge,
  cancelDonateChallenge,
  DonateChallenge,
  MyDonateChallenge,
  ChallengeMode,
} from '../../../api/payment/donate-challenge.api';
import {
  toLocalInput,
  localInputToIso,
  resolveUpdateStartAt,
  challengeRuleSentence,
  formatCa,
  countdownLabel,
  clockSkewMs,
} from '../../../utils/donate-challenge';
import usePollWhile from '../../../hooks/use-poll-while/use-poll-while';

// "Thử thách donate": streamer đặt luật (đua top / về đích), người donate leo bảng xếp
// hạng, hết giờ (hoặc về đích sớm nếu đủ người) thì chốt người thắng và trao vật phẩm.
// Khác "Mục tiêu donate" ở chỗ có bảng xếp hạng + quà cho người thắng, không phải thanh
// tiến độ chung.

type StartMode = 'now' | 'schedule';

const WINNER_COUNT_OPTIONS = [1, 2, 3];

const formatDateTime = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

interface FormState {
  title: string;
  mode: ChallengeMode;
  targetAmount: string;
  winnerCount: string;
  rewardText: string;
  startMode: StartMode;
  startLocal: string;
  endLocal: string;
}

const emptyForm: FormState = {
  title: '',
  mode: 'Top',
  targetAmount: '',
  winnerCount: '1',
  rewardText: '',
  startMode: 'now',
  startLocal: '',
  endLocal: '',
};

const DonateChallengeSetting: React.FC<{ userId?: string }> = ({ userId }) => {
  const overlayUrl = `${window.location.origin}/donate-challenge-screen/${userId}`;

  const [data, setData] = useState<MyDonateChallenge | null>(null);
  const [visible, setVisible] = useState(document.visibilityState === 'visible');
  const [editing, setEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [originalStartMode, setOriginalStartMode] = useState<StartMode>('now');
  const [originalStartLocal, setOriginalStartLocal] = useState('');
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [skewMs, setSkewMs] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [loadError, setLoadError] = useState(false);

  const current = data?.current || null;
  const recent = (data?.recent || []).slice(0, 5);
  const formVisible = editing || !current;
  // Chỉ chặn hiện form rỗng khi CHƯA từng tải được lần nào (data === null) và lần đó lỗi.
  // Nếu đã tải thành công ít nhất 1 lần rồi, current=null nghĩa là server xác nhận thật sự
  // chưa có thử thách -> form trống vẫn đúng, dù lần làm mới sau đó có lỗi mạng.
  const showLoadError = loadError && !data;

  const load = useCallback(() => {
    getMyDonateChallenge()
      .then((res) => {
        setData(res);
        setLoadError(false);
        if (res.current) setSkewMs(clockSkewMs(res.current.serverNow));
      })
      .catch(() => {
        setLoadError(true);
        toast.error('Không tải được thử thách. Vui lòng thử lại.');
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onVis = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  usePollWhile(current?.status === 'Running' && visible, load, 15000);

  // Đếm ngược tại chỗ mỗi giây khi đang Running, không hỏi lại máy chủ liên tục.
  useEffect(() => {
    if (current?.status !== 'Running') return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [current?.status]);

  const countdownMs = current ? new Date(current.endAt).getTime() - (now + skewMs) : 0;

  const openEditForm = (c: DonateChallenge) => {
    const startLocal = toLocalInput(c.startAt);
    setForm({
      title: c.title,
      mode: c.mode,
      targetAmount: c.targetAmount ? `${c.targetAmount}` : '',
      winnerCount: `${c.winnerCount}`,
      rewardText: c.rewardText || '',
      startMode: 'schedule',
      startLocal,
      endLocal: toLocalInput(c.endAt),
    });
    setOriginalStartMode('schedule');
    setOriginalStartLocal(startLocal);
    setEditingId(c.id);
    setEditing(true);
  };

  const closeForm = () => {
    setEditing(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const startBaseMs = (): number => {
    if (form.startMode === 'schedule' && form.startLocal) {
      const d = new Date(form.startLocal);
      if (!Number.isNaN(d.getTime())) return d.getTime();
    }
    return Date.now();
  };

  const setQuickEnd = (minutes: number) => {
    const endIso = new Date(startBaseMs() + minutes * 60000).toISOString();
    setForm((f) => ({ ...f, endLocal: toLocalInput(endIso) }));
  };

  const handleSave = () => {
    if (saving) return; // chặn bấm 2 lần
    const title = form.title.trim();
    if (!title) {
      toast.error('Vui lòng nhập tên thử thách.');
      return;
    }
    const targetAmount = form.mode === 'Race' ? Number(form.targetAmount) : null;
    if (form.mode === 'Race' && (!targetAmount || targetAmount <= 0)) {
      toast.error('Mốc Ca phải lớn hơn 0.');
      return;
    }
    const rewardText = form.rewardText.trim();
    if (!rewardText) {
      toast.error('Vui lòng nhập phần thưởng.');
      return;
    }
    if (form.startMode === 'schedule' && !form.startLocal) {
      toast.error('Vui lòng chọn giờ bắt đầu.');
      return;
    }
    const endAt = localInputToIso(form.endLocal);
    if (!endAt) {
      toast.error('Vui lòng chọn giờ kết thúc.');
      return;
    }

    setSaving(true);
    const request = editingId
      ? updateDonateChallenge(editingId, {
          title,
          mode: form.mode,
          targetAmount,
          winnerCount: Number(form.winnerCount),
          rewardText,
          endAt,
          startAt: resolveUpdateStartAt({
            startMode: form.startMode,
            startLocal: form.startLocal,
            originalStartMode,
            originalStartLocal,
          }),
        })
      : createDonateChallenge({
          title,
          mode: form.mode,
          targetAmount,
          winnerCount: Number(form.winnerCount),
          rewardText,
          endAt,
          startAt: form.startMode === 'now' ? null : localInputToIso(form.startLocal),
        });

    request
      .then(() => {
        closeForm();
        load();
        toast.success('Đã lưu thử thách donate');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Lưu thử thách thất bại');
      })
      .finally(() => setSaving(false));
  };

  const handleCancelChallenge = () => {
    if (!current || cancelling) return;
    if (!window.confirm('Huỷ thử thách donate này? Không thể hoàn tác.')) return;
    setCancelling(true);
    cancelDonateChallenge(current.id)
      .then(() => {
        load();
        toast.success('Đã huỷ thử thách');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Huỷ thử thách thất bại');
      })
      .finally(() => setCancelling(false));
  };

  const copyOverlay = async () => {
    try {
      await navigator.clipboard.writeText(overlayUrl);
      toast.success('Đã sao chép link overlay');
    } catch {
      toast.error('Trình duyệt chặn sao chép — hãy bôi đen link rồi copy tay.');
    }
  };

  const inputCls =
    'h-11 rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-base dark:text-white';

  const canCancel =
    !!current &&
    (current.status === 'Scheduled' ||
      (current.status === 'Running' && !current.hasCountedDonations));

  return (
    <div className="border rounded-xl border-[#DEE0E6] dark:border-slate-700 mt-[10px]">
      <p className="p-3 flex items-center gap-2 w-full text-[#18191E] dark:text-white font-semibold text-[22px] leading-[27px]">
        <Trophy size={20} className="text-blue-600" />
        Thử thách donate
      </p>

      <div className="w-full flex flex-col gap-3 bg-[#f6f7f8] dark:bg-dark2 p-3">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Đặt thử thách có giờ kết thúc cho người xem đua donate. Người thắng nhận phần thưởng
          do bạn tự hứa.
        </p>

        {showLoadError && (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-3">
            <span className="text-sm text-red-600 dark:text-red-400">
              Không tải được thử thách donate.
            </span>
            <button
              type="button"
              onClick={load}
              className="shrink-0 rounded-lg border border-red-300 dark:border-red-700 px-3 py-1 text-sm text-red-600 dark:text-red-400"
            >
              Thử lại
            </button>
          </div>
        )}

        {!showLoadError && !formVisible && current && (
          <div className="rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-800 p-3 flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-sm font-semibold text-gray-800 dark:text-white">
                {current.title}
              </span>
              {current.status === 'Running' && (
                <span className="shrink-0 text-sm font-bold text-blue-600 dark:text-blue-400">
                  {countdownLabel('Running', countdownMs, false)}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {challengeRuleSentence(current.mode, current.targetAmount, current.winnerCount)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Phần thưởng: {current.rewardText}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Bắt đầu {formatDateTime(current.startAt)} · Kết thúc{' '}
              {formatDateTime(current.endAt)}
            </p>

            {current.status === 'Running' && current.leaderboard?.length > 0 && (
              <div className="mt-1 flex flex-col gap-1">
                {current.leaderboard.map((e) => (
                  <div
                    key={e.rank}
                    className="flex items-center justify-between text-xs text-gray-700 dark:text-gray-300"
                  >
                    <span className="truncate">
                      #{e.rank} {e.displayName}
                      {e.isAnonymous && (
                        <span className="ml-1 text-gray-400 dark:text-gray-500">(ẩn danh)</span>
                      )}
                    </span>
                    <span className="shrink-0">{formatCa(e.amount)} Ca</span>
                  </div>
                ))}
              </div>
            )}

            {current.status === 'Running' && (
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Thử thách đã bắt đầu nên không sửa được luật nữa.
              </p>
            )}

            <div className="flex gap-3">
              {current.status === 'Scheduled' && (
                <button
                  type="button"
                  onClick={() => openEditForm(current)}
                  className="rounded-lg border border-[#C8CCD8] dark:border-slate-600 px-3 py-2 text-sm dark:text-white"
                >
                  Sửa
                </button>
              )}
              {canCancel && (
                <button
                  type="button"
                  onClick={handleCancelChallenge}
                  disabled={cancelling}
                  className="rounded-lg border border-red-300 px-3 py-2 text-sm text-red-500 disabled:opacity-60"
                >
                  {cancelling ? 'Đang huỷ…' : 'Huỷ thử thách'}
                </button>
              )}
            </div>
          </div>
        )}

        {!showLoadError && formVisible && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label htmlFor="chal-title" className="text-sm">
                Tên
              </label>
              <input
                id="chal-title"
                value={form.title}
                maxLength={80}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="VD: Ai donate nhiều nhất tối nay"
                className={`${inputCls} w-full`}
              />
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-sm">Kiểu</span>
              <div className="flex gap-4">
                <label className="flex items-center gap-1 cursor-pointer select-none text-sm">
                  <input
                    type="radio"
                    name="chal-mode"
                    checked={form.mode === 'Top'}
                    onChange={() => setForm((f) => ({ ...f, mode: 'Top' }))}
                    className="accent-blue-600"
                  />
                  Đua top
                </label>
                <label className="flex items-center gap-1 cursor-pointer select-none text-sm">
                  <input
                    type="radio"
                    name="chal-mode"
                    checked={form.mode === 'Race'}
                    onChange={() => setForm((f) => ({ ...f, mode: 'Race' }))}
                    className="accent-blue-600"
                  />
                  Về đích
                </label>
              </div>
            </div>

            {form.mode === 'Race' && (
              <div className="flex flex-col gap-1">
                <label htmlFor="chal-target" className="text-sm">
                  Mốc Ca
                </label>
                <input
                  id="chal-target"
                  type="number"
                  min={1}
                  value={form.targetAmount}
                  onChange={(e) => setForm((f) => ({ ...f, targetAmount: e.target.value }))}
                  placeholder="VD: 500000"
                  className={`${inputCls} w-full`}
                />
              </div>
            )}

            <div className="flex flex-col gap-1">
              <label htmlFor="chal-winners" className="text-sm">
                Số người thắng
              </label>
              <select
                id="chal-winners"
                value={form.winnerCount}
                onChange={(e) => setForm((f) => ({ ...f, winnerCount: e.target.value }))}
                className={`${inputCls} w-full`}
              >
                {WINNER_COUNT_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="chal-reward" className="text-sm">
                Phần thưởng
              </label>
              <textarea
                id="chal-reward"
                value={form.rewardText}
                maxLength={120}
                onChange={(e) => setForm((f) => ({ ...f, rewardText: e.target.value }))}
                placeholder="Vd: được gọi tên trên live và chơi 1 trận cùng mình"
                className="min-h-[80px] w-full rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-base dark:text-white"
              />
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-sm">Bắt đầu</span>
              <div className="flex gap-4">
                <label className="flex items-center gap-1 cursor-pointer select-none text-sm">
                  <input
                    type="radio"
                    name="chal-start-mode"
                    checked={form.startMode === 'now'}
                    onChange={() => setForm((f) => ({ ...f, startMode: 'now' }))}
                    className="accent-blue-600"
                  />
                  Ngay bây giờ
                </label>
                <label className="flex items-center gap-1 cursor-pointer select-none text-sm">
                  <input
                    type="radio"
                    name="chal-start-mode"
                    checked={form.startMode === 'schedule'}
                    onChange={() => setForm((f) => ({ ...f, startMode: 'schedule' }))}
                    className="accent-blue-600"
                  />
                  Hẹn giờ
                </label>
              </div>
              {form.startMode === 'schedule' && (
                <input
                  type="datetime-local"
                  value={form.startLocal}
                  onChange={(e) => setForm((f) => ({ ...f, startLocal: e.target.value }))}
                  className={`${inputCls} w-full mt-1`}
                />
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="chal-end" className="text-sm">
                Kết thúc
              </label>
              <input
                id="chal-end"
                type="datetime-local"
                value={form.endLocal}
                onChange={(e) => setForm((f) => ({ ...f, endLocal: e.target.value }))}
                className={`${inputCls} w-full`}
              />
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setQuickEnd(30)}
                  className="rounded-lg border border-[#C8CCD8] dark:border-slate-600 px-3 py-1 text-xs dark:text-white"
                >
                  30 phút
                </button>
                <button
                  type="button"
                  onClick={() => setQuickEnd(60)}
                  className="rounded-lg border border-[#C8CCD8] dark:border-slate-600 px-3 py-1 text-xs dark:text-white"
                >
                  1 giờ
                </button>
                <button
                  type="button"
                  onClick={() => setQuickEnd(120)}
                  className="rounded-lg border border-[#C8CCD8] dark:border-slate-600 px-3 py-1 text-xs dark:text-white"
                >
                  2 giờ
                </button>
              </div>
            </div>

            <div className="flex justify-between gap-3">
              {current && (
                <button
                  type="button"
                  onClick={closeForm}
                  className="rounded-lg border border-[#C8CCD8] dark:border-slate-600 px-4 py-2 text-base dark:text-white"
                >
                  Quay lại
                </button>
              )}
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-lg bg-blue-500 px-4 py-2 text-base text-white disabled:cursor-not-allowed disabled:opacity-60 ml-auto"
              >
                {saving ? 'Đang lưu…' : 'Lưu thử thách'}
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <h2 className="text-sm">Link hiển thị thử thách trên livestream</h2>
          <div className="flex items-center gap-2">
            <div className="flex h-12 min-w-0 flex-1 items-center rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-[#EBECF2] dark:bg-slate-700 px-3 text-base text-[#A1A5B6] dark:text-slate-400">
              <span className="w-full truncate">{overlayUrl}</span>
            </div>
            <button
              type="button"
              onClick={copyOverlay}
              title="Sao chép link"
              className="flex h-12 shrink-0 items-center gap-1 rounded-lg bg-[#39539E] px-4 text-white"
            >
              <Copy size={16} />
              Sao chép
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Dán link này vào phần mềm phát trực tiếp dưới dạng nguồn trình duyệt, kích thước
            gợi ý 600×400.
          </p>
        </div>

        {recent.length > 0 && (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Kết quả gần đây</span>
            {recent.map((r) => (
              <div
                key={r.id}
                className="rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-800 p-3 flex flex-col gap-1"
              >
                <div className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="truncate font-semibold text-gray-800 dark:text-white">
                    {r.title}
                  </span>
                  <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                    {formatDateTime(r.endAt)} · {r.status === 'Cancelled' ? 'Đã huỷ' : 'Đã kết thúc'}
                  </span>
                </div>
                {r.winners?.length > 0 && (
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Người thắng: {r.winners.map((w) => w.displayName).join(', ')}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DonateChallengeSetting;
